import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, SectionHead } from "@/components/Motion";

const TIMELINE = [
  ["2004", "The chip-tuning era", "Regal Tuning begins in the early days of ECU tuning — physically removing and programming ECU chips, one vehicle at a time."],
  ["2008", "OBD revolution", "Serial programming through the diagnostic port transforms the industry. We invest early in genuine tooling and file development."],
  ["2013", "File service launch", "Our calibration files are opened to trade customers — the beginning of the worldwide dealer network."],
  ["2017", "Tricore & Bench/Boot", "Anti-tune protection arrives. We move to Bench and Boot methods and expand our technical support desk."],
  ["2021", "4WD dyno facility", "A dedicated in-house 4WD dynamometer for development, verification and dealer training."],
  ["2026", "1,000+ dealers worldwide", "Regal Tuning supplies files, genuine tools and technical support to tuning businesses across the globe."],
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="container-regal relative">
          <MaskedLines
            lines={["Built from 20+ years", "of tuning experience"]}
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.0] tracking-tight max-w-5xl"
          />
          <Reveal delay={0.5} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
              From physically removing ECU chips in the early 2000s to modern OBD, Bench and Boot calibration — Regal Tuning has grown with every generation of ECU technology.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line py-20 sm:py-24" data-testid="timeline-section">
        <div className="container-regal grid gap-14 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHead
              overline="Timeline"
              title="Two decades of ECU calibration"
              copy="Long-term involvement in professional file development, tool supply and dealer support — all verified on our in-house 4WD dyno."
            />
            <Reveal>
              <div className="overflow-hidden border border-line">
                <img src="/assets/workshop/dyno-911.webp" alt="Regal Tuning 4WD dyno facility" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            <ol className="relative space-y-0 border-l border-line pl-0">
              {TIMELINE.map(([year, title, text], i) => (
                <Reveal key={year} delay={i * 0.05}>
                  <li className="relative grid gap-2 border-b border-line py-7 pl-10 sm:grid-cols-6 sm:gap-6" data-testid={`timeline-${year}`}>
                    <span className="absolute -left-[5px] top-9 h-2.5 w-2.5 rotate-45 bg-regal-amber" />
                    <p className="font-mono text-sm font-bold tracking-[0.2em] text-regal-amber sm:col-span-1">{year}</p>
                    <div className="sm:col-span-5">
                      <p className="font-display text-lg font-bold uppercase tracking-wide">{title}</p>
                      <p className="mt-2 text-sm leading-relaxed text-neutral-400">{text}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="about-today-section">
        <div className="container-regal grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden border border-line">
              <img src="/assets/workshop/dyno-turbos.webp" alt="Porsche Turbo S in the Regal Tuning workshop" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHead
              overline="Today"
              title="A worldwide dealer network"
              copy="Today, Regal Tuning provides professional tuning files, genuine tuning tools and technical support to more than 1,000 dealers worldwide — backed by 4WD dyno development and a technical desk staffed by experienced calibrators."
            />
            <Reveal delay={0.15} className="flex flex-wrap gap-4">
              <Link to="/become-a-dealer" data-testid="about-dealer-cta" className="inline-flex items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-void hover:bg-amber-400 transition-colors">
                Become a Dealer <ArrowRight size={15} />
              </Link>
              <Link to="/contact" data-testid="about-contact-cta" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-neutral-100 hover:border-regal-amber hover:text-regal-amber transition-colors">
                Contact Us
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
